/**
 * SkyPergola Discount System v1.0.0
 * ===================================
 * Metaobject-based discount system replacing:
 *   - product-discount-price.js (~10,000 lines)
 *   - discount-utils.js (~634 lines)
 *   - custom-discount.js (~199 lines)
 *
 * Key differences from the old system:
 *   - Prices are pre-calculated in Liquid (variant-prices JSON)
 *   - Only 1 localStorage key: assemblyPreference ("1" or "2")
 *   - No DOM manipulation for price calculation - JS only shows/hides
 *   - Discount codes read from data attributes on buttons
 *   - Cart drawer items have data-eigenmontage-pct/data-montage-pct attributes
 */
(function () {
  "use strict";

  // ============================================================
  // MODULE: Currency Formatting
  // ============================================================

  var CURRENCY_CONFIG = {
    CHF: { separator: "'", decimal: "." },
    EUR: { separator: ".", decimal: "," },
    AED: { separator: ",", decimal: "." },
    USD: { separator: ",", decimal: "." }
  };

  function getCurrency() {
    return window.shop_currency || "CHF";
  }

  function formatMoney(cents, withCode) {
    var amount = (cents / 100).toFixed(2);
    var currency = getCurrency();
    var config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.CHF;
    var parts = amount.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, config.separator);
    return currency + " " + parts.join(config.decimal);
  }

  function parseMoneyTextToCents(txt) {
    if (!txt) return 0;
    var cleaned = txt.replace(/[A-Za-z\u0600-\u06FF\u00A0]/g, "").trim();
    var m = cleaned.match(/[\d'.,\s]+/);
    if (!m) return 0;
    var s = m[0].trim();
    var lastComma = s.lastIndexOf(",");
    var lastDot = s.lastIndexOf(".");
    if (lastComma > lastDot) {
      s = s.replace(/\./g, "").replace(",", ".");
    } else {
      s = s.replace(/[',\s]/g, "");
    }
    var value = parseFloat(s);
    return isNaN(value) ? 0 : Math.round(value * 100);
  }

  // Export globally for quote-request.js and other consumers
  window.formatMoney = formatMoney;
  window.formatMoneyDrawer = formatMoney;
  window.parseMoneyTextToCents = parseMoneyTextToCents;

  // ============================================================
  // MODULE: Assembly Preference (single localStorage key)
  // ============================================================

  function getAssemblyPreference() {
    try {
      return parseInt(localStorage.getItem("assemblyPreference") || "1", 10);
    } catch (e) {
      return 1;
    }
  }

  function setAssemblyPreference(buttonNumber) {
    try {
      localStorage.setItem("assemblyPreference", String(buttonNumber));
    } catch (e) {}
  }

  function isAssemblyBySkyPergola() {
    return getAssemblyPreference() === 2;
  }

  window.getAssemblyPreference = getAssemblyPreference;
  window.isAssemblyBySkyPergola = isAssemblyBySkyPergola;

  // Legacy compatibility stubs (consumed by quote-request.js)
  window.getDiscountPreference = function () {
    var btn = getAssemblyPreference() === 2
      ? document.getElementById("discount-button-2")
      : document.getElementById("discount-button-1");
    return btn ? (btn.getAttribute("data-discount-code") || btn.getAttribute("data-discount-title") || "") : "";
  };
  window.hasNoDiscountPreference = function () { return false; };
  window.getVariantDiscountData = function (variantId) {
    // In the metaobject system, discount data is on DOM elements, not in localStorage
    var item = document.querySelector(
      '.mini_cart_item[data-variant-id="' + variantId + '"], tr[data-variant-id="' + variantId + '"]'
    );
    if (!item) return null;
    var pref = getAssemblyPreference();
    var pct = pref === 2
      ? parseFloat(item.getAttribute("data-montage-pct") || "0")
      : parseFloat(item.getAttribute("data-eigenmontage-pct") || "0");
    var code = pref === 2
      ? (item.getAttribute("data-montage-code") || "")
      : (item.getAttribute("data-eigenmontage-code") || "");
    if (pct <= 0) return null;
    return { title: code, percentage: pct };
  };
  window.getButtonDiscountMapping = function () {
    var btn1 = document.getElementById("discount-button-1");
    var btn2 = document.getElementById("discount-button-2");
    return {
      button1: btn1 ? (btn1.getAttribute("data-discount-code") || btn1.getAttribute("data-discount-title") || "") : "",
      button2: btn2 ? (btn2.getAttribute("data-discount-code") || btn2.getAttribute("data-discount-title") || "") : ""
    };
  };

  // ============================================================
  // MODULE: Variant Prices (pre-calculated in Liquid)
  // ============================================================

  var variantPricesCache = null;

  function getVariantPrices() {
    if (variantPricesCache) return variantPricesCache;
    var script = document.querySelector('[id^="variant-prices-"]');
    if (!script) return null;
    try {
      variantPricesCache = JSON.parse(script.textContent.trim());
      return variantPricesCache;
    } catch (e) {
      return null;
    }
  }

  // ============================================================
  // MODULE: Price Container Helpers
  // ============================================================

  function getPriceContainer() {
    var el = document.querySelector('[id^="price-"]');
    if (el) return el;
    return document.querySelector(".product-price-container") || null;
  }

  function getCurrentVariantId() {
    var input = document.querySelector('input[name="id"]:checked')
      || document.querySelector('select[name="id"]')
      || document.querySelector('input[name="id"]');
    return input ? input.value : null;
  }

  // ============================================================
  // MODULE: Product Page - Button Toggle
  // ============================================================

  function updateProductPagePrice() {
    var priceContainer = getPriceContainer();
    if (!priceContainer) return;

    var variantId = getCurrentVariantId();
    var prices = getVariantPrices();
    if (!variantId || !prices) return;

    var vData = prices[variantId];
    if (!vData) return;

    var pref = getAssemblyPreference();
    var originalPrice = vData.price;
    var compareAtPrice = vData.compare_at_price || 0;
    var discountAmount = 0;
    var finalPrice = originalPrice;
    var discountCode = "";

    if (pref === 1 && vData.eigenmontage_price !== undefined && vData.eigenmontage_pct > 0) {
      discountAmount = vData.eigenmontage_discount;
      finalPrice = originalPrice - discountAmount;
      discountCode = vData.eigenmontage_code || "";
    } else if (pref === 2 && vData.montage_price !== undefined && vData.montage_pct > 0) {
      discountAmount = vData.montage_discount;
      finalPrice = originalPrice - discountAmount;
      discountCode = vData.montage_code || "";
    }

    // Update price display
    var cce = true;
    var formattedFinal = formatMoney(finalPrice, cce);
    var priceToCompare = compareAtPrice > 0 ? compareAtPrice : originalPrice;

    var priceInsEl = priceContainer.querySelector(".price ins .amount");
    if (priceInsEl) {
      priceInsEl.textContent = formattedFinal;
    } else {
      var priceEl = priceContainer.querySelector(".price .amount:not(.price--compare .amount)");
      if (priceEl && !priceEl.closest(".price--compare")) {
        priceEl.textContent = formattedFinal;
      }
    }

    // Compare price (strikethrough)
    var comparePriceEl = priceContainer.querySelector(".price--compare");
    if (comparePriceEl) {
      var shouldShowCompare = discountAmount > 0 || (compareAtPrice > 0 && compareAtPrice > finalPrice);
      if (shouldShowCompare) {
        var compareAmountEl = comparePriceEl.querySelector(".amount");
        if (compareAmountEl) {
          compareAmountEl.textContent = formatMoney(priceToCompare, cce);
        }
        comparePriceEl.style.display = "inline";
      } else {
        comparePriceEl.style.display = "none";
      }
    }

    // Price --discount class
    var priceInsElement = priceContainer.querySelector(".price ins");
    if (priceInsElement) {
      if (discountAmount > 0) {
        priceInsElement.classList.add("price--discount");
      } else {
        priceInsElement.classList.remove("price--discount");
      }
    }

    // Savings display
    var savingsEl = priceContainer.querySelector(".product-form-saving-price .price");
    if (savingsEl) {
      var totalSavings = discountAmount;
      if (compareAtPrice > 0 && compareAtPrice > finalPrice) {
        totalSavings = compareAtPrice - finalPrice;
      }
      if (totalSavings > 0) {
        savingsEl.textContent = formatMoney(totalSavings, cce);
        savingsEl.closest(".product-form-saving-price").style.display = "block";
      } else {
        savingsEl.closest(".product-form-saving-price").style.display = "none";
      }
    }

    // Applied discount pill
    updateAppliedDiscountPill(discountCode, discountAmount);
  }
  // Export so product.min.js can call after Section Rendering replaces price HTML
  window.updateProductPagePrice = updateProductPagePrice;

  function updateAppliedDiscountPill(code, amount) {
    var el = document.getElementById("applied-discount");
    var textEl = document.getElementById("applied-discount-text");
    if (!el || !textEl) return;

    if (!code || amount <= 0) {
      el.style.display = "none";
      return;
    }

    var codeLabel = window.discountCodeLabel || "Code:";
    var activeText = window.discountActiveText || "Active";
    textEl.textContent = codeLabel + " " + code + " - " + activeText;
    el.style.display = "flex";
  }

  // Button click handler
  window.handleDiscountButtonClick = function (discountTitle, buttonElement) {
    if (!buttonElement) return false;

    var allButtons = document.querySelectorAll(".discount-button");
    allButtons.forEach(function (btn) {
      btn.classList.remove("active");
    });
    buttonElement.classList.add("active");

    var buttonNumber = parseInt(buttonElement.getAttribute("data-button-number") || "1", 10);
    setAssemblyPreference(buttonNumber);

    // Sync cart assembly toggles
    initCartAssemblyToggles();

    updateProductPagePrice();

    // Sync entire cart — global preference changed
    syncMontageInCart(function () {
      syncShippingInCart(function () {
        syncArchitectInCart(function () {
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        });
      });
    });

    return true;
  };

  // ============================================================
  // MODULE: Cart Assembly Toggle
  // ============================================================

  function initCartAssemblyToggles() {
    var pref = getAssemblyPreference();
    var toggles = document.querySelectorAll("[data-cart-assembly-toggle]");
    toggles.forEach(function (toggle) {
      var btn1 = toggle.querySelector('[data-assembly-btn="1"]');
      var btn2 = toggle.querySelector('[data-assembly-btn="2"]');
      if (!btn1 || !btn2) return;
      btn1.classList.toggle("active", pref === 1);
      btn2.classList.toggle("active", pref === 2);
      toggle.removeAttribute("data-syncing");
    });
  }
  window.initCartAssemblyToggles = initCartAssemblyToggles;

  document.addEventListener("click", function (e) {
    var btn = e.target.closest("[data-assembly-btn]");
    if (!btn) return;

    var toggle = btn.closest("[data-cart-assembly-toggle]");
    if (!toggle) return;

    var buttonNumber = parseInt(btn.getAttribute("data-assembly-btn") || "1", 10);
    var currentPref = getAssemblyPreference();

    if (buttonNumber === currentPref) return;
    if (toggle.hasAttribute("data-syncing")) return;

    // Set syncing state on ALL toggles (drawer + page)
    document.querySelectorAll("[data-cart-assembly-toggle]").forEach(function (t) {
      t.setAttribute("data-syncing", "true");
    });

    // Dim cart content while syncing
    var cartDrawer = document.getElementById("Cart-Drawer");
    if (cartDrawer) cartDrawer.classList.add("cart-loading");
    var cartForm = document.querySelector(".thb-cart-form-container");
    if (cartForm) {
      cartForm.style.opacity = "0.5";
      cartForm.classList.add("cart-syncing");
    }

    setAssemblyPreference(buttonNumber);

    // Immediately switch active state on all toggles
    document.querySelectorAll("[data-cart-assembly-toggle]").forEach(function (t) {
      var b1 = t.querySelector('[data-assembly-btn="1"]');
      var b2 = t.querySelector('[data-assembly-btn="2"]');
      if (b1) b1.classList.toggle("active", buttonNumber === 1);
      if (b2) b2.classList.toggle("active", buttonNumber === 2);
    });

    // Sync product page buttons if visible
    var allDiscountButtons = document.querySelectorAll(".discount-button");
    allDiscountButtons.forEach(function (dbtn) {
      var btnNum = parseInt(dbtn.getAttribute("data-button-number") || "0", 10);
      dbtn.classList.toggle("active", btnNum === buttonNumber);
    });

    updateProductPagePrice();

    function clearSyncingState() {
      document.querySelectorAll("[data-cart-assembly-toggle]").forEach(function (t) {
        t.removeAttribute("data-syncing");
      });
      if (cartDrawer) cartDrawer.classList.remove("cart-loading");
      if (cartForm) {
        cartForm.style.opacity = "";
        cartForm.classList.remove("cart-syncing");
      }
    }

    // Safety timeout: always clear syncing state after 12s
    var syncSafetyTimer = setTimeout(clearSyncingState, 12000);

    // Sync Montage + Shipping (they are complementary: Eigenmontage = Versand, Montage = no Versand)
    syncMontageInCart(function () {
      syncShippingInCart(function () {
        clearTimeout(syncSafetyTimer);

        // Cart page: reload to reflect new items (Section Rendering only updates drawer)
        if (/\/cart(\?|$|\/?$)/.test(window.location.pathname)) {
          window.location.reload();
          return;
        }

        clearSyncingState();
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      });
    });
  });

  // ============================================================
  // MODULE: Variant Change Handler
  // ============================================================

  function handleVariantChange() {
    var priceContainer = getPriceContainer();
    if (!priceContainer) return;

    var variantId = getCurrentVariantId();
    if (!variantId) return;

    // Update data attributes on price container
    var prices = getVariantPrices();
    if (prices && prices[variantId]) {
      priceContainer.setAttribute("data-variant-price", prices[variantId].price);
      priceContainer.setAttribute("data-compare-at-price", prices[variantId].compare_at_price || 0);
    }

    updateProductPagePrice();
  }

  // ============================================================
  // MODULE: Localized Property Keys
  // ============================================================

  function getMontagePropertyKey() {
    var labels = { en: "Für", de: "Für", fr: "Pour", it: "Per" };
    var lang = (document.documentElement.lang || "de").split("-")[0].toLowerCase();
    return labels[lang] || "Für";
  }

  /**
   * Build forValue string (product title + variant title) matching the
   * pattern used by sync functions when they read from the Cart API.
   * Reads pre-computed values from Liquid-rendered variant-for-values JSON.
   * Falls back to product title only if data is unavailable.
   */
  function buildForValue() {
    var variantInput = document.querySelector(".js-product-variant-id-input");
    if (variantInput) {
      var script = document.querySelector("[id^=\"variant-for-values-\"]");
      if (script) {
        try {
          var map = JSON.parse(script.textContent);
          var val = map[String(variantInput.value)];
          if (val) return val;
        } catch (e) {}
      }
    }
    // Fallback: product title only
    var productTitle = document.querySelector(".product_title");
    return productTitle ? productTitle.textContent.trim() : "";
  }

  // ============================================================
  // MODULE: Linked Variants Cache
  // ============================================================

  window.linkedVariantsCache = window.linkedVariantsCache || {};

  function getLinkedMontageVariantId(mainVariantId) {
    try {
      var relationships = JSON.parse(localStorage.getItem("montageRelationships") || "{}");
      return relationships[String(mainVariantId)] || null;
    } catch (e) {
      return null;
    }
  }

  function getLinkedShippingVariantId(mainVariantId) {
    try {
      var relationships = JSON.parse(localStorage.getItem("shippingRelationships") || "{}");
      return relationships[String(mainVariantId)] || null;
    } catch (e) {
      return null;
    }
  }

  window.getLinkedMontageVariantId = getLinkedMontageVariantId;
  window.getLinkedShippingVariantId = getLinkedShippingVariantId;

  function setLinkedShippingVariantId(mainVariantId, shippingVariantId) {
    try {
      var rel = JSON.parse(localStorage.getItem("shippingRelationships") || "{}");
      if (shippingVariantId) {
        rel[String(mainVariantId)] = String(shippingVariantId);
      }
      localStorage.setItem("shippingRelationships", JSON.stringify(rel));
    } catch (e) {}
  }

  window.setLinkedShippingVariantId = setLinkedShippingVariantId;

  // Bootstrap localStorage relationships from cart DOM data attributes.
  // This ensures sync functions work even if localStorage was cleared or the
  // product was added from a different session/device.
  function bootstrapRelationshipsFromDOM() {
    // Collect from cart page rows and cart drawer items
    var items = document.querySelectorAll(
      'tr[data-main-variant-id], li[data-main-variant-id]'
    );
    if (items.length === 0) return;

    var montageRel = {};
    var shippingRel = {};
    try { montageRel = JSON.parse(localStorage.getItem("montageRelationships") || "{}"); } catch (e) {}
    try { shippingRel = JSON.parse(localStorage.getItem("shippingRelationships") || "{}"); } catch (e) {}

    var changed = false;
    items.forEach(function (el) {
      var mainId = el.getAttribute("data-main-variant-id");
      if (!mainId) return;

      // Montage: first ID from data-linked-variant-ids
      var linkedIds = el.getAttribute("data-linked-variant-ids");
      if (linkedIds && !montageRel[mainId]) {
        montageRel[mainId] = linkedIds.split(",")[0];
        changed = true;
      }

      // Shipping: from data-linked-shipping-id
      var shippingId = el.getAttribute("data-linked-shipping-id");
      if (shippingId && !shippingRel[mainId]) {
        shippingRel[mainId] = shippingId;
        changed = true;
      }
    });

    if (changed) {
      try { localStorage.setItem("montageRelationships", JSON.stringify(montageRel)); } catch (e) {}
      try { localStorage.setItem("shippingRelationships", JSON.stringify(shippingRel)); } catch (e) {}
    }
  }

  // ============================================================
  // MODULE: Montage/Shipping Sync (ported from old system)
  // ============================================================

  // Retry wrapper for Shopify API calls that may hit 429 rate limits.
  function fetchWithRetry(url, options, retries) {
    retries = retries || 3;
    return fetch(url, options).then(function (res) {
      if (res.status === 429 && retries > 0) {
        var delay = (4 - retries) * 1000;
        return new Promise(function (resolve) {
          setTimeout(resolve, delay);
        }).then(function () {
          return fetchWithRetry(url, options, retries - 1);
        });
      }
      return res;
    });
  }

  // Shopify Cart API is NOT safe for concurrent calls.
  // This helper serializes /cart/change.js operations sequentially.
  function serialCartChanges(ops) {
    return ops.reduce(function (chain, op) {
      return chain.then(function () {
        return fetchWithRetry("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(op)
        });
      });
    }, Promise.resolve());
  }

  function syncMontageInCart(callback, targetVariantIds) {
    var assemblyPreference = localStorage.getItem("assemblyPreference") || "1";
    var propertyKey = getMontagePropertyKey();
    var scoped = targetVariantIds && targetVariantIds.length > 0;

    fetchWithRetry("/cart.js")
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var montageItems = [];
        var mainProducts = [];

        cart.items.forEach(function (item) {
          var isMontage = (item.handle && item.handle.toLowerCase() === "montage") ||
            (item.product_title && item.product_title.toLowerCase().startsWith("montage"));
          var isShipping = (item.handle && item.handle.toLowerCase() === "versandkosten") ||
            (item.product_title && item.product_title.toLowerCase().includes("versandkosten"));
          var isService = item.product_type === "architect" ||
            (item.product_title && item.product_title.toLowerCase().includes("architekt"));

          if (isMontage) {
            montageItems.push(item);
          } else if (!isShipping && !isService) {
            mainProducts.push(item);
          }
        });

        // Scope: only process targeted products
        if (scoped) {
          mainProducts = mainProducts.filter(function (item) {
            return targetVariantIds.indexOf(String(item.variant_id)) !== -1;
          });
        }

        if (assemblyPreference === "2") {
          // Button 2: Add linked Montage for (scoped) main products
          var existingMontageFor = {};
          montageItems.forEach(function (item) {
            if (item.properties && item.properties[propertyKey]) {
              var forValue = item.properties[propertyKey];
              if (!existingMontageFor[forValue]) {
                existingMontageFor[forValue] = { item: item, quantity: item.quantity, variantId: item.variant_id };
              } else {
                existingMontageFor[forValue].quantity += item.quantity;
              }
            }
          });

          var montageToAdd = [];
          var montageToAdjust = [];

          mainProducts.forEach(function (mainItem) {
            var linkedMontageId = getLinkedMontageVariantId(mainItem.variant_id);
            if (!linkedMontageId) return;

            var forValue = mainItem.product_title || mainItem.title || "";
            if (mainItem.variant_title && mainItem.variant_title !== "Default Title") {
              forValue += " " + mainItem.variant_title;
            }

            var neededQty = mainItem.quantity;
            var existing = existingMontageFor[forValue];

            if (existing) {
              if (existing.quantity < neededQty) {
                montageToAdd.push({ forValue: forValue, montageVariantId: linkedMontageId, quantity: neededQty - existing.quantity });
              } else if (existing.quantity > neededQty) {
                montageToAdjust.push({ key: existing.item.key, newQuantity: neededQty });
              }
              existing.handled = true;
            } else {
              montageToAdd.push({ forValue: forValue, montageVariantId: linkedMontageId, quantity: neededQty });
            }
          });

          // Remove orphaned montage (only when NOT scoped)
          var cleanupOps = [];
          if (!scoped) {
            var orphaned = [];
            Object.keys(existingMontageFor).forEach(function (k) {
              if (!existingMontageFor[k].handled) orphaned.push(existingMontageFor[k].item);
            });
            montageItems.forEach(function (item) {
              if (!item.properties || !item.properties[propertyKey]) orphaned.push(item);
            });
            orphaned.forEach(function (item) {
              cleanupOps.push({ id: item.key, quantity: 0 });
            });
          }
          montageToAdjust.forEach(function (adj) {
            cleanupOps.push({ id: adj.key, quantity: adj.newQuantity });
          });

          (cleanupOps.length > 0 ? serialCartChanges(cleanupOps) : Promise.resolve())
            .then(function () {
              if (montageToAdd.length > 0) {
                var items = montageToAdd.map(function (m) {
                  var props = {};
                  props[propertyKey] = m.forValue;
                  return { id: m.montageVariantId, quantity: m.quantity, properties: props };
                });
                return fetchWithRetry("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items: items }) });
              }
            })
            .then(function () { if (callback) callback(); })
            .catch(function () { if (callback) callback(); });
        } else {
          // Button 1: Remove Montage
          var toRemove = montageItems;
          if (scoped) {
            // Only remove montage linked to scoped products
            var scopedForValues = {};
            mainProducts.forEach(function (mainItem) {
              var fv = mainItem.product_title || mainItem.title || "";
              if (mainItem.variant_title && mainItem.variant_title !== "Default Title") fv += " " + mainItem.variant_title;
              scopedForValues[fv] = true;
            });
            toRemove = montageItems.filter(function (item) {
              return item.properties && item.properties[propertyKey] && scopedForValues[item.properties[propertyKey]];
            });
          }
          if (toRemove.length > 0) {
            var removeOps = toRemove.map(function (item) {
              return { id: item.key, quantity: 0 };
            });
            serialCartChanges(removeOps)
              .then(function () { if (callback) callback(); })
              .catch(function () { if (callback) callback(); });
          } else {
            if (callback) callback();
          }
        }
      })
      .catch(function () { if (callback) callback(); });
  }

  window.syncMontageInCart = syncMontageInCart;

  function syncShippingInCart(callback, targetVariantIds) {
    var assemblyPreference = localStorage.getItem("assemblyPreference") || "1";
    var propertyKey = getMontagePropertyKey();
    var scoped = targetVariantIds && targetVariantIds.length > 0;

    fetchWithRetry("/cart.js")
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var shippingItems = [];
        var mainProducts = [];

        cart.items.forEach(function (item) {
          var isShipping = (item.handle && item.handle.toLowerCase() === "versandkosten") ||
            (item.product_title && item.product_title.toLowerCase().includes("versandkosten"));
          var isMontage = (item.handle && item.handle.toLowerCase() === "montage") ||
            (item.product_title && item.product_title.toLowerCase().startsWith("montage"));
          var isService = item.product_type === "architect" ||
            (item.product_title && item.product_title.toLowerCase().includes("architekt"));

          if (isShipping) {
            shippingItems.push(item);
          } else if (!isMontage && !isService) {
            mainProducts.push(item);
          }
        });

        // Scope: only process targeted products
        if (scoped) {
          mainProducts = mainProducts.filter(function (item) {
            return targetVariantIds.indexOf(String(item.variant_id)) !== -1;
          });
        }

        if (assemblyPreference === "1") {
          // Button 1: Add Versandkosten for (scoped) main products
          var existingShippingFor = {};
          shippingItems.forEach(function (item) {
            if (item.properties && item.properties[propertyKey]) {
              var forValue = item.properties[propertyKey];
              if (!existingShippingFor[forValue]) {
                existingShippingFor[forValue] = { item: item, quantity: item.quantity };
              } else {
                existingShippingFor[forValue].quantity += item.quantity;
              }
            }
          });

          var shippingToAdd = [];
          var shippingToAdjust = [];

          mainProducts.forEach(function (mainItem) {
            var forValue = mainItem.product_title || mainItem.title || "";
            if (mainItem.variant_title && mainItem.variant_title !== "Default Title") {
              forValue += " " + mainItem.variant_title;
            }

            var neededQty = mainItem.quantity;
            var existing = existingShippingFor[forValue];

            if (existing) {
              if (existing.quantity < neededQty) {
                shippingToAdd.push({ forValue: forValue, quantity: neededQty - existing.quantity, mainVariantId: mainItem.variant_id });
              } else if (existing.quantity > neededQty) {
                shippingToAdjust.push({ key: existing.item.key, newQuantity: neededQty });
              }
              existing.handled = true;
            } else {
              shippingToAdd.push({ forValue: forValue, quantity: neededQty, mainVariantId: mainItem.variant_id });
            }
          });

          // Remove orphaned shipping (only when NOT scoped)
          var cleanupOps = [];
          if (!scoped) {
            var orphaned = [];
            Object.keys(existingShippingFor).forEach(function (k) {
              if (!existingShippingFor[k].handled) orphaned.push(existingShippingFor[k].item);
            });
            shippingItems.forEach(function (item) {
              if (!item.properties || !item.properties[propertyKey]) orphaned.push(item);
            });
            orphaned.forEach(function (item) {
              cleanupOps.push({ id: item.key, quantity: 0 });
            });
          }
          shippingToAdjust.forEach(function (adj) {
            cleanupOps.push({ id: adj.key, quantity: adj.newQuantity });
          });

          (cleanupOps.length > 0 ? serialCartChanges(cleanupOps) : Promise.resolve())
            .then(function () {
              if (shippingToAdd.length > 0) {
                var itemsPayload = shippingToAdd
                  .map(function (s) {
                    var shippingVariantId = getLinkedShippingVariantId(s.mainVariantId);
                    if (!shippingVariantId) return null;
                    var props = {};
                    props[propertyKey] = s.forValue;
                    return { id: shippingVariantId, quantity: s.quantity, properties: props };
                  })
                  .filter(Boolean);

                if (itemsPayload.length > 0) {
                  return fetchWithRetry("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items: itemsPayload }) });
                }
              }
            })
            .then(function () { if (callback) callback(); })
            .catch(function () { if (callback) callback(); });
        } else {
          // Button 2: Remove Versandkosten
          var toRemove = shippingItems;
          if (scoped) {
            // Only remove shipping linked to scoped products
            var scopedForValues = {};
            mainProducts.forEach(function (mainItem) {
              var fv = mainItem.product_title || mainItem.title || "";
              if (mainItem.variant_title && mainItem.variant_title !== "Default Title") fv += " " + mainItem.variant_title;
              scopedForValues[fv] = true;
            });
            toRemove = shippingItems.filter(function (item) {
              return item.properties && item.properties[propertyKey] && scopedForValues[item.properties[propertyKey]];
            });
          }
          if (toRemove.length > 0) {
            var removeOps = toRemove.map(function (item) {
              return { id: item.key, quantity: 0 };
            });
            serialCartChanges(removeOps)
              .then(function () { if (callback) callback(); })
              .catch(function () { if (callback) callback(); });
          } else {
            if (callback) callback();
          }
        }
      })
      .catch(function () { if (callback) callback(); });
  }

  window.syncShippingInCart = syncShippingInCart;

  // ============================================================
  // MODULE: Architect (Baugesuch) Sync
  // ============================================================

  function getArchitectVariantId() {
    // 1) localStorage (set by product-architect-toggle.liquid)
    var stored = localStorage.getItem("architectVariantId");
    if (stored) return stored;
    // 2) DOM fallback
    var el = document.querySelector("[data-architect-variant-id]");
    return el ? el.getAttribute("data-architect-variant-id") : null;
  }

  function isArchitectSelected() {
    return localStorage.getItem("cartArchitectSelected") === "true";
  }

  function getArchitectEligibleProductIds() {
    try {
      var raw = localStorage.getItem("architectEligibleProducts") || "[]";
      var list = JSON.parse(raw);
      return Array.isArray(list) ? list.map(String) : [];
    } catch (e) { return []; }
  }

  function syncArchitectInCart(callback) {
    var wantArchitect = isArchitectSelected();
    var architectVariantId = getArchitectVariantId();
    if (!architectVariantId) { if (callback) callback(); return; }

    fetchWithRetry("/cart.js")
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var architectItems = [];
        var eligibleTitles = [];
        var propertyKey = getMontagePropertyKey(); // "Für"
        var eligibleIds = getArchitectEligibleProductIds();

        cart.items.forEach(function (item) {
          var isArchitect = item.product_type === "architect" ||
            (item.product_title && item.product_title.toLowerCase().includes("architekt")) ||
            (item.product_title && item.product_title.toLowerCase().includes("baugesuch"));

          if (isArchitect) {
            architectItems.push(item);
            return;
          }

          // Only architect-eligible products count toward Baugesuch add/keep + "Für" list
          if (eligibleIds.indexOf(String(item.product_id)) !== -1) {
            var title = item.product_title || "";
            if (item.variant_title && item.variant_title !== "Default Title") {
              title += " " + item.variant_title;
            }
            if (title && eligibleTitles.indexOf(title) === -1) {
              eligibleTitles.push(title);
            }
          }
        });

        var forValue = eligibleTitles.join(", ");
        var hasEligibleProduct = eligibleTitles.length > 0;

        if (wantArchitect && hasEligibleProduct) {
          if (architectItems.length === 0) {
            // Add exactly 1 Baugesuch
            var props = {};
            if (forValue) props[propertyKey] = forValue;
            return fetchWithRetry("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ items: [{ id: parseInt(architectVariantId), quantity: 1, properties: props }] })
            });
          }

          // Already have architect item(s) — ensure exactly 1 with qty 1 + updated Für
          var keepItem = architectItems[0];
          var currentFor = (keepItem.properties && keepItem.properties[propertyKey]) || "";

          // Remove duplicates first
          if (architectItems.length > 1) {
            var dupeUpdates = {};
            for (var i = 1; i < architectItems.length; i++) {
              dupeUpdates[architectItems[i].key] = 0;
            }
            fetchWithRetry("/cart/update.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ updates: dupeUpdates })
            });
          }

          // Update Für property or fix quantity via /cart/change.js
          if (keepItem.quantity !== 1 || currentFor !== forValue) {
            var changeProps = {};
            changeProps[propertyKey] = forValue;
            return fetchWithRetry("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id: keepItem.key, quantity: 1, properties: changeProps })
            });
          }
        } else {
          // Remove all architect items
          if (architectItems.length > 0) {
            var updates = {};
            architectItems.forEach(function (item) { updates[item.key] = 0; });
            return fetchWithRetry("/cart/update.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ updates: updates })
            });
          }
        }
      })
      .then(function () { if (callback) callback(); })
      .catch(function (err) {
        console.error("[SKY-DISCOUNT] syncArchitectInCart error:", err);
        if (callback) callback();
      });
  }

  window.syncArchitectInCart = syncArchitectInCart;

  // ============================================================
  // MODULE: Checkout Redirect
  // ============================================================

  function getActiveDiscountCode() {
    var pref = getAssemblyPreference();
    var btn = pref === 2
      ? document.getElementById("discount-button-2")
      : document.getElementById("discount-button-1");

    if (btn) {
      return btn.getAttribute("data-discount-code") || btn.getAttribute("data-discount-title") || "";
    }

    // Fallback: read from cart drawer items
    var items = document.querySelectorAll("#Cart-Drawer .mini_cart_item, tr[id^='CartItem-']");
    var code = "";
    items.forEach(function (item) {
      if (code) return;
      if (pref === 1) {
        code = item.getAttribute("data-eigenmontage-code") || "";
      } else {
        code = item.getAttribute("data-montage-code") || "";
      }
    });
    return code;
  }

  function validateTermsCheckbox(context) {
    var selectors;
    if (context === "drawer") {
      selectors = [
        "#CartDrawerTerms",
        '#Cart-Drawer input[type="checkbox"][required]'
      ];
    } else if (context === "cart") {
      selectors = [
        "#CartTerms",
        '#cart-form input[type="checkbox"][required]',
        '.cart-collaterals-terms input[type="checkbox"][required]'
      ];
    } else {
      // Fallback: check only visible required checkboxes
      selectors = [
        "#CartTerms",
        "#CartDrawerTerms",
        '#cart-form input[type="checkbox"][required]',
        '#Cart-Drawer input[type="checkbox"][required]',
        '.cart-collaterals-terms input[type="checkbox"][required]'
      ];
    }
    for (var i = 0; i < selectors.length; i++) {
      var cb = document.querySelector(selectors[i]);
      if (cb && cb.required && !cb.checked) {
        cb.focus();
        try { cb.reportValidity(); } catch (e) {}
        return false;
      }
    }
    return true;
  }

  function performCheckoutRedirect(code) {
    var url = "/checkout";
    if (code && code.trim() !== "") {
      url = "/checkout?discount=" + encodeURIComponent(code.trim());
    }
    window.location.href = url;
    return true;
  }

  function setupCheckoutRedirects() {
    if (document.documentElement.dataset.skyChkBound) return;
    document.documentElement.dataset.skyChkBound = "true";

    // Click delegation for checkout buttons
    document.addEventListener("click", function (e) {
      var checkoutBtn = e.target.closest('button[name="checkout"], a[href*="/checkout"]');
      if (!checkoutBtn) return;

      var isDrawer = checkoutBtn.closest("#Cart-Drawer");
      var isCart = checkoutBtn.closest("#cart-form, form#cart-form");
      if (!isCart && checkoutBtn.getAttribute("form") === "cart-form") isCart = true;

      if (!isDrawer && !isCart) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (!validateTermsCheckbox(isDrawer ? "drawer" : "cart")) return false;

      // Show loading
      checkoutBtn.classList.add("thb-loading");
      checkoutBtn.disabled = true;

      // Run final Montage/Shipping/Architect reconcile before checkout
      syncMontageInCart(function () {
        syncShippingInCart(function () {
          syncArchitectInCart(function () {
            var code = getActiveDiscountCode();
            setTimeout(function () {
              performCheckoutRedirect(code);
            }, 200);
          });
        });
      });

      return false;
    }, true);

    // Form submit delegation
    document.addEventListener("submit", function (e) {
      var form = e.target;
      if (!form || form.tagName !== "FORM") return;

      var isDrawer = form.closest("#Cart-Drawer");
      var isCart = form.id === "cart-form" || form.closest("#cart-form");
      var hasCheckout = form.querySelector('button[name="checkout"]');
      var hasExtCheckout = document.querySelector('button[name="checkout"][form="' + form.id + '"]');

      if (!(isDrawer || isCart) || !(hasCheckout || hasExtCheckout)) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (!validateTermsCheckbox(isDrawer ? "drawer" : "cart")) return false;

      syncMontageInCart(function () {
        syncShippingInCart(function () {
          syncArchitectInCart(function () {
            var code = getActiveDiscountCode();
            setTimeout(function () {
              performCheckoutRedirect(code);
            }, 200);
          });
        });
      });

      return false;
    }, true);

    // Convert checkout buttons to type="button"
    var cartBtn = document.querySelector('form#cart-form button[name="checkout"], button[form="cart-form"][name="checkout"]');
    if (cartBtn && !cartBtn.dataset.skyBound) {
      cartBtn.dataset.skyBound = "true";
      cartBtn.type = "button";
    }
    var drawerBtn = document.querySelector('#Cart-Drawer button[name="checkout"]');
    if (drawerBtn && !drawerBtn.dataset.skyBound) {
      drawerBtn.dataset.skyBound = "true";
      drawerBtn.type = "button";
    }
  }

  // ============================================================
  // MODULE: Cart Drawer Discount Application
  // ============================================================

  function applyCartDrawerDiscounts() {
    var pref = getAssemblyPreference();
    var drawerItems = document.querySelectorAll("#Cart-Drawer .mini_cart_item");
    var drawerTotals = { original: 0, discounted: 0 };

    drawerItems.forEach(function (item) {
      var handle = item.getAttribute("data-product-handle") || "";
      var isMontage = handle === "montage" || handle === "montage-zubehor";
      if (isMontage) {
        var pill = item.querySelector(".custom-discount-pill");
        if (pill) pill.remove();
        // Include Montage prices in subtotal (no discount applied, but counted)
        var origLine = parseInt(item.getAttribute("data-original-line-price") || "0", 10);
        var finalLine = parseInt(item.getAttribute("data-final-line-price") || "0", 10);
        drawerTotals.original += origLine;
        drawerTotals.discounted += finalLine;
        return;
      }

      // Non-campaign items: keep Liquid-rendered prices (incl. automatic discounts)
      var hasCampaignData = item.hasAttribute("data-eigenmontage-code") || item.hasAttribute("data-montage-code");
      if (!hasCampaignData) {
        var origLine = parseInt(item.getAttribute("data-original-line-price") || "0", 10);
        var finalLine = parseInt(item.getAttribute("data-final-line-price") || "0", 10);
        drawerTotals.original += origLine;
        drawerTotals.discounted += finalLine;
        return;
      }

      var pctAttr = pref === 1 ? "data-eigenmontage-pct" : "data-montage-pct";
      var codeAttr = pref === 1 ? "data-eigenmontage-code" : "data-montage-code";
      var pct = parseFloat(item.getAttribute(pctAttr) || "0");
      var code = item.getAttribute(codeAttr) || "";
      var originalPrice = parseInt(item.getAttribute("data-original-price") || "0", 10);

      var priceSpan = item.querySelector(".price");
      if (!priceSpan) return;

      var qtyText = item.querySelector(".mini-quantity");
      var quantity = 1;
      if (qtyText) {
        var qtyMatch = qtyText.textContent.match(/\d+/);
        if (qtyMatch) quantity = parseInt(qtyMatch[0]);
      }

      if (pct > 0 && originalPrice > 0) {
        var discountAmount = Math.round(originalPrice * (pct / 100));
        var finalPrice = originalPrice - discountAmount;

        priceSpan.innerHTML =
          '<span class="mini-quantity">' + quantity + " ×</span> " +
          '<del class="price--compare"><span class="amount">' + formatMoney(originalPrice) + "</span></del>" +
          '<ins class="price--sale"><span class="amount">' + formatMoney(finalPrice) + "</span></ins>";
        priceSpan.classList.remove("campaign-price-pending");
        priceSpan.classList.add("campaign-price-ready");

        drawerTotals.original += originalPrice * quantity;
        drawerTotals.discounted += finalPrice * quantity;

        // Add/update discount pill
        var discountIcon = '<svg width="12" height="12" aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></svg>';
        var existingPill = item.querySelector(".custom-discount-pill");
        if (!existingPill && code) {
          var pillHtml =
            '<div class="custom-discount-pill">' +
            "<span>" + discountIcon + "</span>" +
            "<span>" + code + "</span>" +
            "</div>";
          var content = item.querySelector(".mini_cart_item_content");
          if (content) {
            var priceEl = content.querySelector(".price");
            if (priceEl) {
              priceEl.insertAdjacentHTML("beforebegin", pillHtml);
            }
          }
        } else if (existingPill && code) {
          existingPill.querySelector("span:last-child").textContent = code;
        } else if (existingPill && !code) {
          existingPill.remove();
        }
      } else {
        // Campaign item but no discount for this button: show original price
        priceSpan.innerHTML =
          '<span class="mini-quantity">' + quantity + " ×</span> " +
          '<span class="amount">' + formatMoney(originalPrice) + "</span>";
        priceSpan.classList.remove("campaign-price-pending");
        priceSpan.classList.add("campaign-price-ready");

        var existingPill = item.querySelector(".custom-discount-pill");
        if (existingPill) existingPill.remove();

        drawerTotals.original += originalPrice * quantity;
        drawerTotals.discounted += originalPrice * quantity;
      }
    });

    // Update subtotal
    updateDrawerSubtotal(drawerTotals);
  }

  window.applyCartDrawerDiscounts = applyCartDrawerDiscounts;

  function updateDrawerSubtotal(totals) {
    var row = document.getElementById("cart-drawer-subtotal");
    if (!row) return;

    var amountEl = row.querySelector(".amount");
    if (!amountEl) return;

    var totalDiscount = totals.original - totals.discounted;
    if (totalDiscount > 0) {
      amountEl.innerHTML =
        "<del><span>" + formatMoney(totals.original) + "</span></del> " +
        "<ins>" + formatMoney(totals.discounted) + "</ins>";
      row.setAttribute("data-original-total", String(totals.original));
      row.setAttribute("data-final-total", String(totals.discounted));
    } else {
      amountEl.textContent = formatMoney(totals.original);
      row.setAttribute("data-original-total", String(totals.original));
      row.setAttribute("data-final-total", String(totals.original));
    }
  }

  // ============================================================
  // MODULE: Cart Page Discount Application
  // ============================================================

  function applyCartPageDiscounts() {
    if (!/\/cart(\?|$|\/?$)/.test(window.location.pathname)) return;

    var pref = getAssemblyPreference();
    var cartRows = document.querySelectorAll('tr[id^="CartItem-"]');
    var base = 0;
    var totalDiscount = 0;

    cartRows.forEach(function (row) {
      var handle = row.getAttribute("data-product-handle") || "";
      var isMontage = handle === "montage" || handle === "montage-zubehor";
      var variantPrice = parseInt(row.getAttribute("data-variant-price") || "0", 10);
      var qtyInput = row.querySelector('input[name="updates[]"]');
      var quantity = qtyInput ? parseInt(qtyInput.value || "1") : 1;
      var lineOriginal = variantPrice * quantity;

      if (isMontage) {
        // Include Montage prices in subtotal (no discount applied, but counted)
        base += lineOriginal;
        return;
      }

      base += lineOriginal;

      // Non-campaign items: use Liquid's final_line_price for Shopify automatic discounts
      var hasCampaignData = row.hasAttribute("data-eigenmontage-code") || row.hasAttribute("data-montage-code");
      if (!hasCampaignData) {
        var finalLine = parseInt(row.getAttribute("data-final-line-price") || "0", 10);
        if (finalLine > 0 && finalLine < lineOriginal) {
          totalDiscount += (lineOriginal - finalLine);
        }
        return;
      }

      var pctAttr = pref === 1 ? "data-eigenmontage-pct" : "data-montage-pct";
      var codeAttr = pref === 1 ? "data-eigenmontage-code" : "data-montage-code";
      var pct = parseFloat(row.getAttribute(pctAttr) || "0");
      var code = row.getAttribute(codeAttr) || "";
      var nameCell = row.querySelector(".product-name");
      var discountIcon = '<svg width="12" height="12" aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></svg>';

      if (pct > 0 && variantPrice > 0) {
        // Per-unit rounding for consistency between price column and subtotal column
        var unitDiscount = Math.round(variantPrice * (pct / 100));
        var unitFinal = variantPrice - unitDiscount;
        var lineDiscount = unitDiscount * quantity;
        var lineFinal = unitFinal * quantity;
        totalDiscount += lineDiscount;

        // Update subtotal cell
        var subtotalCell = row.querySelector(".product-subtotal .price");
        if (subtotalCell) {
          subtotalCell.innerHTML =
            '<del><span class="amount">' + formatMoney(lineOriginal) + "</span></del>" +
            '<ins><span class="amount">' + formatMoney(lineFinal) + "</span></ins>";
          subtotalCell.classList.remove("campaign-price-pending");
          subtotalCell.classList.add("campaign-price-ready");
        }

        // Update price cell (same per-unit calculation)
        var priceCell = row.querySelector(".product-price .price");
        if (priceCell) {
          priceCell.innerHTML =
            '<del><span class="amount">' + formatMoney(variantPrice) + "</span></del>" +
            '<ins><span class="amount">' + formatMoney(unitFinal) + "</span></ins>";
          priceCell.classList.remove("campaign-price-pending");
          priceCell.classList.add("campaign-price-ready");
        }

        // Update mobile price cell (visible < 768px)
        var mobilePrice = row.querySelector(".product-mobile-price");
        if (mobilePrice) {
          mobilePrice.innerHTML =
            '<del><span class="amount">' + formatMoney(variantPrice) + "</span></del> " +
            '<ins><span class="amount">' + formatMoney(unitFinal) + "</span></ins>";
        }

        // Add/update discount pill in product-name cell
        if (nameCell) {
          var existingPill = nameCell.querySelector(".custom-discount-pill");
          if (!existingPill && code) {
            var pillHtml =
              '<div class="custom-discount-pill">' +
              "<span>" + discountIcon + "</span>" +
              "<span>" + code + " (-" + pct + "%)</span>" +
              "</div>";
            var autoDiscountList = nameCell.querySelector(".discounts");
            if (autoDiscountList) {
              autoDiscountList.insertAdjacentHTML("beforebegin", pillHtml);
            } else {
              nameCell.insertAdjacentHTML("beforeend", pillHtml);
            }
          } else if (existingPill && code) {
            existingPill.querySelector("span:last-child").textContent = code + " (-" + pct + "%)";
          } else if (existingPill && !code) {
            existingPill.remove();
          }
        }
      } else {
        // Campaign item but no discount for this preference: remove pill if present
        if (nameCell) {
          var existingPill = nameCell.querySelector(".custom-discount-pill");
          if (existingPill) existingPill.remove();
        }
      }
    });

    // Update cart subtotal row
    var subtotalRow = document.querySelector("tr.cart-subtotal");
    if (subtotalRow) {
      var amountWrap = subtotalRow.querySelector(".price");
      if (amountWrap && base > 0) {
        var finalCents = Math.max(0, base - totalDiscount);
        if (totalDiscount > 0) {
          amountWrap.innerHTML =
            '<del><span class="amount">' + formatMoney(base) + "</span></del> " +
            '<ins><span class="amount">' + formatMoney(finalCents) + "</span></ins>";
        } else {
          amountWrap.innerHTML = '<span class="amount">' + formatMoney(base) + "</span>";
        }
        subtotalRow.setAttribute("data-original-total", String(base));
        subtotalRow.setAttribute("data-final-total", String(finalCents));

        // Sync sticky footer price (MutationObserver dies after innerHTML replacement)
        var stickyPrice = document.querySelector(".cart-sticky-footer__price .price");
        if (stickyPrice) {
          stickyPrice.innerHTML = amountWrap.innerHTML;
          stickyPrice.setAttribute("data-original-total", String(base));
          stickyPrice.setAttribute("data-final-total", String(finalCents));
        }
      }
    }
  }

  window.applyCartPageDiscounts = applyCartPageDiscounts;

  // ============================================================
  // MODULE: Form Interception (Add to Cart)
  // ============================================================

  function initializeFormInterception() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    if (forms.length === 0) return;

    forms.forEach(function (form) {
      if (form.dataset.skyFormBound) return;
      form.dataset.skyFormBound = "true";

      form.addEventListener("submit", function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        var variantInput = form.querySelector('input[name="id"]') || form.querySelector('select[name="id"]');
        var variantId = variantInput ? variantInput.value : null;
        if (!variantId) return;

        var quantityInput = form.querySelector('input[name="quantity"]');
        var quantity = quantityInput ? parseInt(quantityInput.value || "1") : 1;

        var submitButtons = form.querySelectorAll(".single-add-to-cart-button");
        submitButtons.forEach(function (btn) {
          btn.setAttribute("aria-disabled", true);
          btn.classList.add("thb-loading");
        });

        // Store relationships for later Montage/Shipping sync
        storeVariantRelationships(variantId);

        // Build items array: main product + linked variants if Button 2
        var pref = getAssemblyPreference();
        var linkedScript = document.querySelector('[id^="linked-variants-data-"]');
        var shippingScript = document.querySelector('[id^="shipping-variants-data-"]');
        var items = [{ id: parseInt(variantId), quantity: quantity }];

        if (pref === 2 && linkedScript) {
          try {
            var linkedData = JSON.parse(linkedScript.textContent.trim());
            var linked = linkedData[variantId];
            if (linked && Array.isArray(linked)) {
              linked.forEach(function (lv) {
                if (lv.id) {
                  // Store Montage relationship
                  try {
                    var rel = JSON.parse(localStorage.getItem("montageRelationships") || "{}");
                    rel[String(variantId)] = String(lv.id);
                    localStorage.setItem("montageRelationships", JSON.stringify(rel));
                  } catch (ex) {}

                  var props = {};
                  props[getMontagePropertyKey()] = buildForValue();
                  items.push({ id: parseInt(lv.id), quantity: quantity, properties: props });
                }
              });
            }
          } catch (ex) {}
        } else if (pref === 1 && shippingScript) {
          // Add shipping product
          try {
            var shippingData = JSON.parse(shippingScript.textContent.trim());
            var shippingVariantId = shippingData[variantId];
            if (shippingVariantId) {
              setLinkedShippingVariantId(variantId, shippingVariantId);
              var props = {};
              props[getMontagePropertyKey()] = buildForValue();
              items.push({ id: parseInt(shippingVariantId), quantity: quantity, properties: props });
            }
          } catch (ex) {}
        }

        fetchWithRetry("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest", Accept: "application/json" },
          body: JSON.stringify({ items: items })
        })
          .then(function (res) {
            if (!res.ok) throw new Error("Failed to add to cart");
            return res.json();
          })
          .then(function (cartData) {
            // Sync Montage/Shipping/Architect for the just-added product only
            var scope = [String(variantId)];
            return new Promise(function (resolve) {
              syncMontageInCart(function () {
                syncShippingInCart(function () {
                  syncArchitectInCart(function () {
                    resolve(cartData);
                  }, scope);
                }, scope);
              }, scope);
            });
          })
          .then(function (cartData) {
            // Refresh cart drawer via Section Rendering API
            return fetch("/cart.js?sections=cart-drawer&sections_url=" + encodeURIComponent(window.location.pathname))
              .then(function (res) { return res.json(); })
              .then(function (data) {
                var drawer = document.getElementById("Cart-Drawer");
                if (drawer && data.sections && data.sections["cart-drawer"]) {
                  var content = drawer.querySelector(".side-panel-content");
                  if (content) {
                    content.innerHTML = data.sections["cart-drawer"];
                  }
                  if (typeof CartDrawer !== "undefined") {
                    try { new CartDrawer(); } catch (ex) {}
                  }
                }
                return cartData;
              });
          })
          .then(function (cartData) {
            submitButtons.forEach(function (btn) {
              btn.classList.remove("thb-loading");
              btn.removeAttribute("aria-disabled");
            });

            document.dispatchEvent(new CustomEvent("cart:refresh"));

            // Open cart drawer
            var toggle = document.getElementById("cart-drawer-toggle");
            if (toggle) {
              setTimeout(function () { toggle.click(); }, 100);
              setTimeout(function () { document.dispatchEvent(new CustomEvent("cart-drawer:open")); }, 200);
            }

            // Apply discounts to refreshed drawer
            setTimeout(function () { applyCartDrawerDiscounts(); }, 400);
          })
          .catch(function (error) {
            console.error("[SKY-DISCOUNT] Error adding to cart:", error);
            submitButtons.forEach(function (btn) {
              btn.classList.remove("thb-loading");
              btn.removeAttribute("aria-disabled");
            });
          });

        return false;
      }, true);
    });
  }

  function storeVariantRelationships(variantId) {
    // Store shipping relationship from product page data
    var shippingScript = document.querySelector('[id^="shipping-variants-data-"]');
    if (shippingScript) {
      try {
        var data = JSON.parse(shippingScript.textContent.trim());
        if (data[variantId]) {
          setLinkedShippingVariantId(variantId, data[variantId]);
        }
      } catch (e) {}
    }

    // Store montage relationship from product page data
    var linkedScript = document.querySelector('[id^="linked-variants-data-"]');
    if (linkedScript) {
      try {
        var data = JSON.parse(linkedScript.textContent.trim());
        var linked = data[variantId];
        if (linked && Array.isArray(linked) && linked.length > 0) {
          var rel = JSON.parse(localStorage.getItem("montageRelationships") || "{}");
          rel[String(variantId)] = String(linked[0].id);
          localStorage.setItem("montageRelationships", JSON.stringify(rel));
        }
      } catch (e) {}
    }
  }

  // ============================================================
  // MODULE: Essential Upsell Integration
  // ============================================================

  function handleCartItemAdded(item) {
    if (!item) return;
    // Dispatch for Essential Upsell and other apps
    try {
      document.dispatchEvent(new CustomEvent("cart:item-added", { detail: { product: item } }));
    } catch (e) {}
  }

  // Upsell add-to-cart is handled by upsell.js
  function setupUpsellHandlers() {
    // Native upsell: handled by assets/upsell.js
  }

  // ============================================================
  // MODULE: Session Reconciliation
  // ============================================================

  function reconcileSession() {
    fetchWithRetry("/cart.js")
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || cart.items.length === 0) {
          // Cart empty: cleanup
          try {
            localStorage.removeItem("montageRelationships");
            localStorage.removeItem("shippingRelationships");
          } catch (e) {}
          return;
        }

        var pref = getAssemblyPreference();
        var hasMontage = cart.items.some(function (item) {
          return (item.handle === "montage" || item.handle === "montage-zubehor");
        });
        var hasShipping = cart.items.some(function (item) {
          return (item.handle === "versandkosten") ||
            (item.product_title && item.product_title.toLowerCase().includes("versandkosten"));
        });

        var hasArchitect = cart.items.some(function (item) {
          return item.product_type === "architect" ||
            (item.product_title && item.product_title.toLowerCase().includes("architekt")) ||
            (item.product_title && item.product_title.toLowerCase().includes("baugesuch"));
        });
        var wantArchitect = isArchitectSelected();

        // Any inconsistency → full sync of Montage, Shipping, and Architect
        var needsSync = (hasMontage && pref === 1) ||  // Montage present but Eigenmontage selected
          (hasShipping && pref === 2) ||                // Versandkosten present but Montage selected
          (hasMontage && hasShipping) ||                // Both present (should never happen)
          (hasArchitect && !wantArchitect) ||           // Architect present but not selected
          (!hasArchitect && wantArchitect);             // Architect missing but selected

        if (needsSync) {
          syncMontageInCart(function () {
            syncShippingInCart(function () {
              syncArchitectInCart(function () {
                document.dispatchEvent(new CustomEvent("cart:refresh"));
              });
            });
          });
        }
      })
      .catch(function () {});
  }

  // ============================================================
  // MODULE: Cart Refresh Event Handler
  // ============================================================

  function onCartRefresh() {
    setTimeout(function () {
      applyCartDrawerDiscounts();
      applyCartPageDiscounts();
      setupCheckoutRedirects();
      initCartAssemblyToggles();
    }, 300);
  }

  // ============================================================
  // MODULE: Legacy localStorage Cleanup
  // ============================================================

  function cleanupLegacyLocalStorage() {
    var legacyKeys = [
      "activeDiscountTitle",
      "variantDiscounts",
      "cartLockedDiscountPreference",
      "buttonDiscountMapping",
      "noDiscountVariants",
      "autoDiscountDisabled"
    ];
    legacyKeys.forEach(function (key) {
      try { localStorage.removeItem(key); } catch (e) {}
    });
  }

  // ============================================================
  // MODULE: Discount Removal (button on pills)
  // ============================================================

  window.removeDiscount = function (variantId) {
    // In the metaobject system, "removing" a discount means switching to the button
    // that has no discount for this product. For now, just refresh the cart.
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  };

  // ============================================================
  // INITIALIZATION
  // ============================================================

  function init() {
    // Clean up legacy localStorage keys on first load
    cleanupLegacyLocalStorage();

    // Restore button state from assemblyPreference
    var pref = getAssemblyPreference();
    var btn1 = document.getElementById("discount-button-1");
    var btn2 = document.getElementById("discount-button-2");
    if (btn1 && btn2) {
      if (pref === 2) {
        btn1.classList.remove("active");
        btn2.classList.add("active");
      } else {
        btn1.classList.add("active");
        btn2.classList.remove("active");
      }
    }

    // Init cart assembly toggles
    initCartAssemblyToggles();

    // Apply prices on product page
    updateProductPagePrice();

    // Set up form interception
    initializeFormInterception();

    // Set up checkout redirects
    setupCheckoutRedirects();

    // Set up upsell handlers
    setupUpsellHandlers();

    // Discount code is applied only at checkout via /checkout?discount=CODE
    // No session sync needed - cart discounts are displayed by JS from metaobject data

    // Apply discounts in cart drawer/page
    applyCartDrawerDiscounts();
    applyCartPageDiscounts();

    // Listen for variant changes
    document.addEventListener("change", function (e) {
      if (e.target.matches('input[name="id"], select[name="id"]')) {
        setTimeout(handleVariantChange, 50);
      }
    });
    document.addEventListener("variantChange", handleVariantChange);
    document.addEventListener("product:variant-change", function () {
      handleVariantChange();
    });

    // Listen for cart refresh events
    ["cart:refresh", "cart:updated", "theme:cart:changed", "thb:cart:update", "cart-drawer:refresh"].forEach(function (evt) {
      document.addEventListener(evt, onCartRefresh);
    });

    // cart:refresh:complete fires after cart.min.js has replaced the main-cart HTML.
    // Apply discounts immediately (no timeout) so campaign-price-pending classes are
    // removed on the freshly-rendered rows. Without this, the fixed 300ms delay in
    // onCartRefresh can fire before the /cart fetch returns, leaving prices hidden.
    document.addEventListener("cart:refresh:complete", function () {
      applyCartDrawerDiscounts();
      applyCartPageDiscounts();
      setupCheckoutRedirects();
      initCartAssemblyToggles();
      document.querySelectorAll(".campaign-price-pending").forEach(function (el) {
        el.classList.add("campaign-price-ready");
      });
    });

    // Cart item change events
    ["line-item:change:end"].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        var detail = e.detail || {};

        // Any qty change (including removal) reconciles linked items:
        // - removal: clears orphaned Montage/Versandkosten/Architect
        // - increase/decrease: syncs linked Montage Zubehör qty to match main product qty
        syncMontageInCart(function () {
          syncShippingInCart(function () {
            syncArchitectInCart(function () {
              document.dispatchEvent(new CustomEvent("cart:refresh"));
            });
          });
        });
      });
    });

    // Bootstrap localStorage relationships from DOM (covers cleared storage / new sessions)
    bootstrapRelationshipsFromDOM();

    // Session reconciliation
    reconcileSession();

    // Safety fallback: reveal any campaign prices that JS failed to process
    setTimeout(function () {
      document.querySelectorAll(".campaign-price-pending").forEach(function (el) {
        el.classList.remove("campaign-price-pending");
        el.classList.add("campaign-price-ready");
      });
    }, 3000);
  }

  // Clear stuck syncing state on any page restore (browser back, bfcache, etc.)
  function clearAllSyncingStates() {
    document.querySelectorAll("[data-syncing]").forEach(function (el) {
      el.removeAttribute("data-syncing");
    });
    var cartDrawer = document.querySelector(".side-panel");
    if (cartDrawer) cartDrawer.classList.remove("cart-loading");
    var cartForm = document.querySelector(".thb-cart-form-container");
    if (cartForm) cartForm.classList.remove("cart-syncing");
    // Reset stuck checkout buttons (thb-loading + disabled from checkout redirect)
    document.querySelectorAll('button[name="checkout"].thb-loading').forEach(function (btn) {
      btn.classList.remove("thb-loading");
      btn.disabled = false;
    });
  }
  window.addEventListener("pageshow", function () {
    clearAllSyncingStates();
    initCartAssemblyToggles();
    applyCartDrawerDiscounts();
    applyCartPageDiscounts();
  });

  // Bootstrap
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Also run on full load for late-rendering elements
  window.addEventListener("load", function () {
    setTimeout(function () {
      applyCartDrawerDiscounts();
      applyCartPageDiscounts();
      setupCheckoutRedirects();
      initCartAssemblyToggles();
    }, 500);
  });

  // MutationObserver: re-apply discounts when cart drawer content changes
  // (catches Section Rendering API updates from upsell.js and other sources)
  var drawerObserverTimer = null;
  function observeCartDrawer() {
    var target = document.querySelector("#Cart-Drawer .side-panel-content");
    if (!target) return;
    new MutationObserver(function () {
      clearTimeout(drawerObserverTimer);
      drawerObserverTimer = setTimeout(function () {
        applyCartDrawerDiscounts();
        setupCheckoutRedirects();
        initCartAssemblyToggles();
      }, 300);
    }).observe(target, { childList: true, subtree: true });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", observeCartDrawer);
  } else {
    observeCartDrawer();
  }

})();
